// src/components/Blocks/TCEvents/View.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Loader,
  Message,
  Button,
  Accordion,
  Icon,
  Card,
  List,
  Label,
} from 'semantic-ui-react';
import { format, parseISO } from 'date-fns';
import './TCEvents.css';
import downIcon from '@plone/volto/icons/collapse-down.svg';
import upIcon from '@plone/volto/icons/collapse-up.svg';

const TCEventsView = ({ data }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const {
    title = '',
    displayMode = 'accordion',
    maxEvents = 10,
    showEventType = true,
    showSpeaker = true,
    showDateTime = true,
    showDescription = true,
  } = data || {};

  // Memoize maxEvents to ensure it's stable
  const memoizedMaxEvents = useMemo(() => {
    return typeof maxEvents === 'number' && maxEvents > 0 ? maxEvents : 3;
  }, [maxEvents]);

  // Fetch events from API
  const fetchEvents = useCallback(async () => {
    try {
      //console.log('Fetching events from API...');

      setLoading(true);
      setError(null);

      const response = await fetch(
        'https://events.tifrbng.res.in/api/latest?format=json',
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const eventsData = await response.json();
      //console.log('📦 Received events:', eventsData.length);

      // Validate data structure
      if (!Array.isArray(eventsData)) {
        throw new Error('API did not return an array of events');
      }

      // Sort by start date (newest first)
      const sortedEvents = eventsData.sort((a, b) => {
        const dateA = new Date(a.start);
        const dateB = new Date(b.start);
        return dateB - dateA;
      });

      // Limit to maxEvents
      const limitedEvents = eventsData.slice(0, memoizedMaxEvents);
      // const limitedEvents = sortedEvents.slice(0, memoizedMaxEvents);
      //console.log(
      //  '📏 Limited to:',
      //  memoizedMaxEvents,
      //  'Final count:',
      //  limitedEvents.length,
      //);

      //setEvents(limitedEvents);
      setEvents(eventsData);
      setLoading(false);
      //console.log('✅ Events loaded successfully');
    } catch (err) {
      //console.error('❌ Error fetching events:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [memoizedMaxEvents]);

  // Single useEffect with stable dependencies
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleAccordionClick = useCallback(
    (e, titleProps) => {
      const { index } = titleProps;
      const newIndex = activeIndex === index ? -1 : index;
      setActiveIndex(newIndex);
    },
    [activeIndex],
  );

  const formatDate = useCallback((dateString) => {
    try {
      return format(parseISO(dateString), 'EEE, MMMM dd, yyyy');
    } catch {
      return dateString;
    }
  }, []);

  const formatTime = useCallback((dateString) => {
    try {
      return format(parseISO(dateString), 'h:mm a');
    } catch {
      return '';
    }
  }, []);

  const getEventTypeColor = useCallback((type) => {
    const colors = {
      Colloquium: '#666',
      Seminar: 'green',
      Lecture: 'orange',
      'Thesis Defense': 'red',
      Workshop: 'purple',
      Conference: 'teal',
    };
    return colors[type] || 'grey';
  }, []);

  // Render Accordion Mode
  const renderAccordionMode = () => (
    <div className="events-list accordion-mode">
      <Accordion styled fluid>
        {events.map((event, index) => (
          <div key={event.id} className="event-item">
            <Accordion.Title
              active={activeIndex === index}
              index={index}
              onClick={handleAccordionClick}
              className="event-title-section"
            >
              <div className="event-header">
                <div className="event-title-content">
                  <div className="event-meta">
                    {showEventType && (
                      <Label
                        color="#666"
                        size="medium"
                        className="event-type-label"
                      >
                        {event.etype}
                      </Label>
                    )}
                    {showDateTime && (
                      <span className="date-text">
                        {formatDate(event.start)} • {formatTime(event.start)}
                        {event.end && ` - ${formatTime(event.end)}`}
                      </span>
                    )}
                  </div>
                  <h3 className="event-title">{event.title}</h3>
                  {showSpeaker && event.speaker && (
                    <div className="speaker-preview">
                      <strong>Speaker:</strong> {event.speaker.first_name}{' '}
                      {event.speaker.last_name}, {event.speaker.affiliation}
                    </div>
                  )}
                  {event.location && (
                    <div className="speaker-preview">
                      <strong>Location:</strong> {event.location}
                    </div>
                  )}
                </div>
                <Icon name={activeIndex === index ? upIcon : downIcon} />
              </div>
            </Accordion.Title>

            <Accordion.Content
              active={activeIndex === index}
              className="event-content"
            >
              <div className="event-details">
                {showDescription && event.description && (
                  <div className="description-section">
                    <h4 className="section-header">Abstract</h4>
                    <div className="event-description">
                      <div
                        dangerouslySetInnerHTML={{ __html: event.description }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </Accordion.Content>
          </div>
        ))}
      </Accordion>
    </div>
  );

  // Render Simple List Mode
  const renderSimpleMode = () => (
    <div className="events-list simple-mode">
      <List divided relaxed>
        {events.map((event) => (
          <List.Item key={event.id} className="simple-event-item">
            <div className="simple-event-content">
              <div className="simple-event-header">
                <h3 className="simple-event-title">{event.title}</h3>
                {showEventType && (
                  <Label color={getEventTypeColor(event.etype)} size="small">
                    {event.etype}
                  </Label>
                )}
              </div>

              <div className="simple-event-details">
                {showSpeaker && event.speaker && (
                  <div className="simple-speaker">
                    <Icon name="user" />
                    <span>
                      <strong>
                        {event.speaker.first_name} {event.speaker.last_name}
                      </strong>
                      {event.speaker.affiliation && (
                        <span className="speaker-affiliation">
                          {' '}
                          - {event.speaker.affiliation}
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {showDateTime && (
                  <div className="simple-datetime">
                    <Icon name="calendar" />
                    <span>
                      {formatDate(event.start)} at {formatTime(event.start)}
                      {event.end && ` - ${formatTime(event.end)}`}
                    </span>
                  </div>
                )}

                {event.location && (
                  <div className="simple-location">
                    <Icon name="map marker" />
                    <span>{event.location}</span>
                  </div>
                )}
              </div>
            </div>
          </List.Item>
        ))}
      </List>
    </div>
  );

  if (loading) {
    return (
      <Container className="tcevents-block">
        <Loader active inline="centered" size="large">
          Loading events...
        </Loader>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="tcevents-block">
        <Message negative>
          <Message.Header>Error loading events</Message.Header>
          <p>{error}</p>
          <Button
            primary
            onClick={() => fetchEvents()}
            icon="refresh"
            content="Retry"
          />
        </Message>
      </Container>
    );
  }

  return (
    <Container className="tcevents-block">
      {title && <h2 className="block-title">{title}</h2>}

      {events.length === 0 ? (
        <Message info>
          <Message.Header>No events found</Message.Header>
          <p>There are no events available at the moment.</p>
          <Button
            basic
            onClick={() => fetchEvents()}
            icon="refresh"
            content="Refresh Events"
          />
        </Message>
      ) : (
        <>
          {displayMode === 'accordion'
            ? renderAccordionMode()
            : renderSimpleMode()}
        </>
      )}
    </Container>
  );
};

export default TCEventsView;
